import { useTxGuard } from '@cronos-app/components';
import { getChainOfLayer } from '@cronos-app/sdk';
import { currentWallet } from '@cronos-app/wallet';
import { useToast } from '@ui/hooks/useToast';
import { queryClient } from '@ui/utils/queryClient';
import { useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';

import type { ExtendedTokenInfo } from '../types';
import { useCardLayer } from '../useCardContextStore';
import { useCdcDepositCardService } from './useCdcDepositCardService';
import { useSwapAndTopUpInfo } from './useSwapAndTopUpInfo';
import { useTopUpHistoryDepositAddressesKey } from './useTopUpHistoryList';

export const useSwapAndTopUp = (from: ExtendedTokenInfo, amount: string) => {
  const t = useTranslations();
  const network = currentWallet.useDesiredNetwork();
  const layer = useCardLayer();
  const { data } = useSwapAndTopUpInfo(from, amount);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const service = useCdcDepositCardService();
  const swapAndTopUp = useTxGuard(
    useCallback(
      async ({ provider }) => {
        if (!data) throw new Error('Data is not provided');
        setLoading(true);
        try {
          const tx = await service.exec_swapAndTopUp(
            data,
            await provider.getSigner(),
            true,
          );
          toast({
            status: 'success',
            title: t(`card.It's done!`),
            description: t(
              'card.Your top-up went through successfully Please confirm this transaction in your Crypto-com app!',
            ),
            txId: tx.hash,
            duration: null,
          });
          setLoading(false);
          queryClient.invalidateQueries({
            queryKey: [
              'getTokenBalanceQuery',
              useTopUpHistoryDepositAddressesKey,
            ],
          });
          return tx;
        } catch (e) {
          toast({
            status: 'error',
            title: t('card.Transaction Failed!'),
            // description: e instanceof Error ? e.message : String(e),
            description: t('card.Something went wrong, please try again!'),
            duration: null,
          });
        }
        setLoading(false);
      },
      [data, service, t, toast],
    ),
    getChainOfLayer(network, layer),
  );
  return { swapAndTopUp, loading };
};
